/**
 * ActiveBlockFlattener — Obsidian-style live preview for TipTap/ProseMirror.
 *
 * When the cursor enters a textblock, inline marks (bold, italic, code, etc.)
 * are converted to their raw markdown syntax as editable plain text.
 * When the cursor leaves, the raw text is parsed back into rich-text marks.
 */
import { Extension } from '@tiptap/react'
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state'
import { Fragment, type Mark, type Node as PMNode, type Schema } from '@tiptap/pm/model'
import type { EditorView } from '@tiptap/pm/view'

const pluginKey = new PluginKey('activeBlockFlattener')
const META_KEY = 'abf:pos'
const FOCUS_META = 'abf:focus'

// ---- Marks we flatten ----
const KNOWN_MARKS = new Set(['bold', 'italic', 'code', 'strike', 'link'])

function isKnownMark(m: Mark): boolean {
  return KNOWN_MARKS.has(m.type.name)
}

// ---- Serializer: marks → markdown text ----

function openSyntax(mark: Mark): string {
  switch (mark.type.name) {
    case 'bold': return '**'
    case 'italic': return '*'
    case 'code': return '`'
    case 'strike': return '~~'
    case 'link': return '['
    default: return ''
  }
}

function closeSyntax(mark: Mark): string {
  switch (mark.type.name) {
    case 'bold': return '**'
    case 'italic': return '*'
    case 'code': return '`'
    case 'strike': return '~~'
    case 'link': return `](${mark.attrs?.href ?? ''})`
    default: return ''
  }
}

function serializeToMarkdown(content: Fragment): { text: string; cursorMap: number[] } {
  let text = ''
  const cursorMap: number[] = []
  let openMarks: Mark[] = []
  let origOffset = 0

  content.forEach((child) => {
    if (!child.isText || !child.text) {
      // Close all open marks before non-text node
      for (let i = openMarks.length - 1; i >= 0; i--) {
        text += closeSyntax(openMarks[i])
      }
      openMarks = []
      if (child.type.name === 'hardBreak') {
        cursorMap[origOffset] = text.length
        text += '\n'
        origOffset += 1
      }
      return
    }

    const newMarks = child.marks.filter(isKnownMark)

    // Find longest common prefix of mark arrays
    let commonLen = 0
    while (
      commonLen < openMarks.length &&
      commonLen < newMarks.length &&
      openMarks[commonLen].eq(newMarks[commonLen])
    ) {
      commonLen++
    }

    // Close marks that diverged (reverse order for correct nesting)
    for (let i = openMarks.length - 1; i >= commonLen; i--) {
      text += closeSyntax(openMarks[i])
    }

    // Open new marks
    for (let i = commonLen; i < newMarks.length; i++) {
      text += openSyntax(newMarks[i])
    }

    openMarks = [...newMarks]

    // Map each character: original offset → markdown offset
    for (let i = 0; i < child.text.length; i++) {
      cursorMap[origOffset + i] = text.length + i
    }

    text += child.text
    origOffset += child.text.length
  })

  // Close remaining open marks
  for (let i = openMarks.length - 1; i >= 0; i--) {
    text += closeSyntax(openMarks[i])
  }

  // Map one past the end
  cursorMap[origOffset] = text.length

  return { text, cursorMap }
}

function hasInlineMarks(content: Fragment): boolean {
  let found = false
  content.forEach((child) => {
    if (child.isText && child.marks.some(isKnownMark)) {
      found = true
    }
  })
  return found
}

// ---- Block-level prefix helpers ----

function extractHeadingPrefix(text: string): { level: number; rest: string } | null {
  const match = text.match(/^(#{1,6}) /)
  if (match) return { level: match[1].length, rest: text.slice(match[0].length) }
  return null
}

// ---- Parser: markdown text → ProseMirror Fragment ----
// Self-contained state-machine parser — no dependency on markdown-it internals.
// Handles: **bold**, *italic*, `code`, ~~strike~~, [text](url)

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function parseMarkdownToFragment(text: string, editor: any): Fragment {
  if (!text) return Fragment.empty

  const schema: Schema = editor.schema
  const nodes: PMNode[] = []
  const activeMarks = new Map<string, Mark>()
  let buffer = ''

  function flushBuffer() {
    if (!buffer) return
    const marks = Array.from(activeMarks.values())
    nodes.push(schema.text(buffer, marks))
    buffer = ''
  }

  function toggleMark(name: string) {
    flushBuffer()
    if (activeMarks.has(name)) {
      activeMarks.delete(name)
    } else {
      const markType = schema.marks[name]
      if (markType) {
        activeMarks.set(name, markType.create())
      }
    }
  }

  let i = 0
  while (i < text.length) {
    // *** — bold + italic together
    if (text[i] === '*' && text[i + 1] === '*' && text[i + 2] === '*') {
      flushBuffer()
      // If both open → close both; else open both
      if (activeMarks.has('bold') && activeMarks.has('italic')) {
        activeMarks.delete('italic')
        activeMarks.delete('bold')
      } else {
        if (!activeMarks.has('bold') && schema.marks.bold) {
          activeMarks.set('bold', schema.marks.bold.create())
        }
        if (!activeMarks.has('italic') && schema.marks.italic) {
          activeMarks.set('italic', schema.marks.italic.create())
        }
      }
      i += 3
      continue
    }

    // ** — bold
    if (text[i] === '*' && text[i + 1] === '*') {
      toggleMark('bold')
      i += 2
      continue
    }

    // ~~ — strikethrough
    if (text[i] === '~' && text[i + 1] === '~') {
      toggleMark('strike')
      i += 2
      continue
    }

    // * — italic (single, not part of **)
    if (text[i] === '*') {
      toggleMark('italic')
      i += 1
      continue
    }

    // ` — code
    if (text[i] === '`') {
      toggleMark('code')
      i += 1
      continue
    }

    // [text](url) — link
    if (text[i] === '[') {
      const closeBracket = text.indexOf('](', i + 1)
      if (closeBracket > i) {
        const closeParen = text.indexOf(')', closeBracket + 2)
        if (closeParen > closeBracket + 2) {
          flushBuffer()
          const linkText = text.slice(i + 1, closeBracket)
          const href = text.slice(closeBracket + 2, closeParen)
          const linkMark = schema.marks.link?.create({ href })
          if (linkMark) {
            const marks = [...Array.from(activeMarks.values()), linkMark]
            nodes.push(schema.text(linkText, marks))
          } else {
            nodes.push(schema.text(linkText, Array.from(activeMarks.values())))
          }
          i = closeParen + 1
          continue
        }
      }
    }

    // Regular character
    buffer += text[i]
    i++
  }

  // Flush remaining buffer
  flushBuffer()

  if (nodes.length === 0) {
    return Fragment.from(schema.text(text))
  }

  return Fragment.from(nodes)
}

// ---- Safe replaceWith that doesn't throw ----

function safeReplaceContent(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tr: any,
  blockPos: number,
  node: PMNode,
  content: Fragment,
): boolean {
  try {
    tr.replaceWith(blockPos + 1, blockPos + node.nodeSize - 1, content)
    return true
  } catch (e) {
    console.warn('[ActiveBlockFlattener] replaceWith error:', e)
    return false
  }
}

// ---- Unflatten helpers ----

// Unflatten a single block inside an existing transaction.
// Handles heading prefix detection and node type restoration.
function unflattenInTr(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tr: any,
  pos: number,
  node: PMNode,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  editor: any,
): boolean {
  const rawText = node.textContent
  if (!rawText) return false

  const heading = extractHeadingPrefix(rawText)
  const textToParse = heading ? heading.rest : rawText

  const parsedContent = textToParse
    ? parseMarkdownToFragment(textToParse, editor)
    : Fragment.empty

  if (parsedContent.size === 0 && !heading) return false

  if (parsedContent.size > 0) {
    if (!safeReplaceContent(tr, pos, node, parsedContent)) return false
  } else {
    // Empty heading content — just remove the prefix text
    try {
      tr.replaceWith(pos + 1, pos + node.nodeSize - 1, Fragment.empty)
    } catch {
      return false
    }
  }

  if (heading) {
    try {
      tr.setNodeMarkup(pos, editor.schema.nodes.heading, { level: heading.level })
    } catch { /* ignore */ }
  }

  return true
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function unflattenBlock(view: EditorView, pos: number, editor: any) {
  const node = view.state.doc.nodeAt(pos)
  if (!node || !node.isTextblock) return

  const tr = view.state.tr
  if (!unflattenInTr(tr, pos, node, editor)) return

  tr.setMeta(META_KEY, null)
  tr.setMeta('addToHistory', false)
  view.dispatch(tr)
}

// ---- Extension ----

export const ActiveBlockFlattener = Extension.create({
  name: 'activeBlockFlattener',

  addProseMirrorPlugins() {
    const editorInstance = this.editor

    return [
      new Plugin({
        key: pluginKey,

        state: {
          init(): { pos: number | null; hasFocus: boolean } {
            return { pos: null, hasFocus: false }
          },
          apply(tr, value): { pos: number | null; hasFocus: boolean } {
            const focusMeta = tr.getMeta(FOCUS_META)
            if (focusMeta !== undefined) {
              return { ...value, hasFocus: focusMeta }
            }
            const meta = tr.getMeta(META_KEY)
            if (meta !== undefined) {
              return { ...value, pos: meta }
            }
            if (value.pos !== null && tr.docChanged) {
              return { ...value, pos: tr.mapping.map(value.pos) }
            }
            return value
          },
        },

        props: {
          handleDOMEvents: {
            focus(view) {
              view.dispatch(view.state.tr.setMeta(FOCUS_META, true))
              return false
            },
            blur(view) {
              const ps = pluginKey.getState(view.state) as { pos: number | null; hasFocus: boolean } | undefined
              if (ps?.pos != null) {
                unflattenBlock(view, ps.pos, editorInstance)
              }
              view.dispatch(view.state.tr.setMeta(FOCUS_META, false))
              return false
            },
          },
        },

        appendTransaction(transactions, _oldState, newState) {
          // Skip our own transactions
          if (transactions.some((t) => t.getMeta(META_KEY) !== undefined)) return null

          const { $head } = newState.selection
          const depth = $head.depth

          // Determine active textblock position
          let newActivePos: number | null = null
          if (depth > 0) {
            const nodePos = $head.before(depth)
            const node = newState.doc.nodeAt(nodePos)
            if (node && node.isTextblock && node.type.name !== 'codeBlock') {
              newActivePos = nodePos
            }
          }

          const ps = pluginKey.getState(newState) as { pos: number | null; hasFocus: boolean }
          const flatPos = ps.pos

          // If editor has no focus, only unflatten (don't flatten)
          if (!ps.hasFocus) {
            if (flatPos !== null) {
              const oldNode = newState.doc.nodeAt(flatPos)
              if (oldNode && oldNode.isTextblock) {
                const tr = newState.tr
                if (unflattenInTr(tr, flatPos, oldNode, editorInstance)) {
                  tr.setMeta(META_KEY, null)
                  tr.setMeta('addToHistory', false)
                  return tr
                }
              }
            }
            return null
          }

          // Check if input rules re-added marks or restored heading type
          let needsReflatten = false
          if (newActivePos !== null && newActivePos === flatPos) {
            const node = newState.doc.nodeAt(newActivePos)
            if (node && (hasInlineMarks(node.content) || node.type.name === 'heading')) {
              needsReflatten = true
            }
          }

          const blockChanged = newActivePos !== flatPos
          if (!blockChanged && !needsReflatten) return null

          const tr = newState.tr

          // 1. Unflatten the previously flattened block
          if (flatPos !== null && blockChanged) {
            const oldNode = tr.doc.nodeAt(flatPos)
            if (oldNode && oldNode.isTextblock) {
              unflattenInTr(tr, flatPos, oldNode, editorInstance)
            }
          }

          // 2. Flatten the new active block (or re-flatten current)
          let didFlatten = false
          const targetPos = blockChanged ? newActivePos : flatPos
          if (targetPos !== null) {
            const mappedPos = blockChanged ? tr.mapping.map(targetPos) : targetPos
            const targetNode = tr.doc.nodeAt(mappedPos)
            const isHeading = targetNode?.type.name === 'heading'

            if (targetNode && targetNode.isTextblock && (hasInlineMarks(targetNode.content) || isHeading)) {
              let { text, cursorMap } = serializeToMarkdown(targetNode.content)

              // Prepend heading prefix
              if (isHeading) {
                const prefix = '#'.repeat(targetNode.attrs.level as number) + ' '
                text = prefix + text
                cursorMap = cursorMap.map((p) => p + prefix.length)
              }

              if (text) {
                const textNode = newState.schema.text(text)
                if (safeReplaceContent(tr, mappedPos, targetNode, Fragment.from(textNode))) {
                  didFlatten = true

                  // Convert heading to paragraph for editing
                  if (isHeading) {
                    try {
                      tr.setNodeMarkup(mappedPos, newState.schema.nodes.paragraph)
                    } catch { /* ignore */ }
                  }

                  // Restore cursor at the right position in the flattened text
                  if (blockChanged && depth > 0) {
                    const origOffset = $head.pos - $head.start(depth)
                    const mdOffset = cursorMap[origOffset] ?? text.length
                    const newCursorPos = mappedPos + 1 + Math.min(mdOffset, text.length)
                    try {
                      tr.setSelection(TextSelection.create(tr.doc, newCursorPos))
                    } catch {
                      // let ProseMirror handle the cursor mapping
                    }
                  }
                }
              }
            }
          }

          if (!tr.docChanged) return null

          // Only track as flattened if we actually flattened the block
          let finalPos: number | null = null
          if (didFlatten && targetPos !== null) {
            finalPos = blockChanged
              ? tr.mapping.map(newActivePos!)
              : flatPos
          }

          tr.setMeta(META_KEY, finalPos)
          tr.setMeta('addToHistory', false)
          return tr
        },
      }),
    ]
  },

  onCreate() {
    // Monkey-patch getMarkdown so it serializes the flattened block correctly
    const editor = this.editor
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const storage = editor.storage?.markdown as any
    if (!storage?.getMarkdown) return

    const originalGetMarkdown: () => string = storage.getMarkdown

    storage.getMarkdown = () => {
      const state = editor.state
      const ps = pluginKey.getState(state) as { pos: number | null; hasFocus: boolean } | undefined
      const flatPos = ps?.pos

      if (flatPos == null) return originalGetMarkdown()

      const node = state.doc.nodeAt(flatPos)
      if (!node || !node.isTextblock) return originalGetMarkdown()

      // Build a temp doc with the flattened block restored to rich-text
      try {
        const tempTr = state.tr
        if (!unflattenInTr(tempTr, flatPos, node, editor)) return originalGetMarkdown()
        return storage.serializer.serialize(tempTr.doc)
      } catch {
        return originalGetMarkdown()
      }
    }
  },
})
